/**
 * Resolve a person's name as typed in a spreadsheet onto a roster row.
 *
 * Extracted from the onboarding importer so the task importer can use the same
 * rule — and so it is testable at all: vitest does not collect `.tsx`, which is
 * where this used to live.
 *
 * Deliberately conservative. It matches a full name, initials, or a bare first
 * name, and nothing else: an unmatched name is reported to the user as a row
 * error, which is far cheaper to fix than a task silently assigned to the wrong
 * person because a fuzzy match looked close enough.
 */

export interface MatchableEmployee {
  id: string
  name: string
  initials: string
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

export function matchEmployee<T extends MatchableEmployee>(
  name: string,
  employees: T[],
): T | undefined {
  const n = norm(name)
  if (!n) return undefined
  return employees.find(
    (e) => norm(e.name) === n || norm(e.initials) === n || norm(e.name.split(/\s+/)[0] ?? '') === n,
  )
}
